import { useEffect, useState, useRef, Fragment, memo, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as IconSearch } from 'bootstrap-icons/icons/search.svg'
import { ReactComponent as IconBoxArrowUpRight } from 'bootstrap-icons/icons/box-arrow-up-right.svg'
import { ReactComponent as IconTrash } from 'bootstrap-icons/icons/trash.svg'
import { ReactComponent as IconArrowCounterclockwise } from 'bootstrap-icons/icons/arrow-counterclockwise.svg'


import Fuse from 'fuse.js'
import deburr from 'lodash/deburr'

import useMutation from 'hooks/useMutation'
import useQuery from 'hooks/useQuery'
import styles from './styles.module.scss'
import { Prescriptions } from './Prescriptions/Prescriptions'
import { BetaFeedback } from 'Components/BetaFeedback/BetaFeedback'

let CONSULTATIONS_STEP = 30

// Card V3 (feedback Lucio, 2026-08-28): copy de los estados terminales del
// pipeline de resumen de IA que NO son 'saved' — mismo patrón que
// STATUS_LABEL de HceSummary.js (mapa status→texto en español, t()+
// fallback, sin JSON de traducciones que tocar). Tono gris/tranquilizador
// (alert-secondary, no alert-danger) — mismo criterio que HceSummary.js
// para no alarmar de más por una falla de una feature beta.
const AI_SUMMARY_FAILURE_LABEL = {
  discarded_empty_transcript: 'No se pudo transcribir el audio de la consulta — completá la evolución a mano.',
  discarded_partial_transcript: 'No se pudo transcribir el audio de una de las partes — se guardó la transcripción disponible como borrador para revisar.',
  summary_failed: 'Se transcribió el audio pero no se pudo generar el resumen — se guardó la transcripción como borrador para revisar.',
  save_failed: 'El resumen se generó pero no se pudo guardar — se guardó como borrador para revisar.',
  timed_out: 'Está tardando más de lo esperado — revisá en unos minutos, puede que ya esté disponible.',
  // [Fase G, 2026-09-03] a diferencia de los otros — acá el resumen SÍ se
  // generó y SÍ se guardó como evolución real (no un borrador de
  // respaldo): el fallback genérico ("no se pudo generar") sería falso acá.
  // El único matiz es que el último tramo de audio de alguna de las partes
  // no llegó a confirmarse a tiempo (consulta larga) — el resumen mismo ya
  // trae esa advertencia visible, este cartel solo redirige a mirarlo.
  saved_incomplete: 'El resumen ya está disponible en la evolución — revisalo, puede faltar el tramo final del audio de alguna de las partes.',
}

export function Archive(props) {
  const [file, setFile] = useState({ ...props.file })
  // Bug real (auditoría 2026-08-14): sin onError, un toggle archivar/
  // restaurar que fallaba en el server dejaba `file` pegado al valor
  // optimista (ej. "restaurado" localmente aunque el server lo rechazó),
  // sin ningún aviso — se guarda acá el valor previo al toggle para poder
  // revertir a ESE estado, no al `props.file` inicial (que puede haber
  // quedado desactualizado si el padre no lo vuelve a pasar).
  const previousFileRef = useRef(file)
  const [mutate, mutation] = useMutation(
    window._l(`/records`).substr(1),
    {
      onMutate: payload => setFile({ ...payload }),
      onError: () => {
        setFile({ ...previousFileRef.current })
        window.notify({
          message: t('ehr:file_archive_error', 'No se pudo actualizar el archivo — intentá de nuevo.'),
          type: 'danger',
          title: 'Error',
        })
      },
    }
  );
  const { t } = useTranslation()

  const onToggleFile = file => e => {
    e.preventDefault()
    if (!file.id) return
    previousFileRef.current = file
    if (!file.deleted && window.confirm(t('ehr:file_remove_confirmation', 'Confirmas eliminar el archivo?'))) {
      mutate({ ...file, deleted: true })
    } else if (file.deleted) {
      mutate({ ...file, deleted: false })
    }
    return false
  }

  return (
    <div
      className={props.className}
      style={{ opacity: file.deleted ? '0.5' : '' }}
    >
      <a
        href={file.link}
        rel="noopener noreferrer"
        target="_blank"
        className="text-sm d-flex align-items-center flex-wrap gap-1"
        data-tooltip-id="app-tooltip"
        data-tooltip-place="right"
        data-tooltip-content={file.notes}
      >
        <IconBoxArrowUpRight width="12" height="12" className="text-secondary" />
        {/* Bug real (Trello "V2 | Arreglos de Responsive Design", seguimiento
            2026-09-11, Juan Cruz QA): file.name sin puntos de corte (sin
            espacios) en un flex sin wrap desbordaba esta fila y el
            overflow-x:hidden global de index.scss lo tapaba (mismo tipo de
            bug ya corregido en Consultation.js, auditoría 2026-08-18, para
            el otro listado de archivos de HCE — acá faltaba el mismo
            text-break/flex-wrap). */}
        <div className="flex-fill">
          <span className={`text-break ${!!file.notes ? 'border-bottom border-2 border-info' : ''}`}>{file.name}</span>
        </div>
        {props.user.uid === file.createdBy && file.id && file.createdAt >= (Date.now() - 48 * 60 * 60 * 1000) && (
          <div>
            <button className="btn btn-sm p-0 lh-1 mt-n1" onClick={onToggleFile(file)}>
              {file.deleted ? (
                <IconArrowCounterclockwise width="14" height="14" className="text-success" />
              ) : (
                <IconTrash width="13" height="13" className="text-danger" />
              )}
            </button>
          </div>
        )}
      </a>
    </div>
  )
}

// Componente lazy para lazy loading
const LazyConsultation = lazy(() => import('./Consultation'));


function Consultations(props) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [showAllFiles, setShowAllFiles] = useState(false)
  const [consultationsNumber, setConsultationsNumber] = useState(CONSULTATIONS_STEP)

  const [groups, setGroups] = useState()
  const fuse = useRef(null)
  const aiSummaryFeedbackRef = useRef(null)
  
  // Query para obtener recursos - se ejecuta una sola vez para todas las consultas
  const resources = useQuery(window._l("/resources").substr(1) + "?deleted=true");
  
  useEffect(() => {
    if (Array.isArray(props.consultations.groups)) {
      const groups = { all: [] }
      for (const group of props.consultations.groups) {
        groups.all.push({
          group,
          text: props.consultations[group].evolutions.map(e => e.text).join(" \n ")
        })
      }

      groups.results = groups.all

      setGroups(groups)

      fuse.current = new Fuse(groups.all, {
        keys: ['text'],
        threshold: 0.3,
        ignoreLocation: true
      })
    }
  }, [props.consultations.groups])

  const search = ({ target: { value } }) => {
    const query = deburr(value?.trim() || '')
    setQuery(value)
    let payload = { ...groups }

    if (!query) {
      payload.results = [...groups.all]
    } else {
      payload.results = fuse.current.search(query).flatMap(i => i.item) || []
    }

    setGroups(JSON.clone(payload))
  }

  const handleEditPrescriptionVisibility = (prescription) => {
    // Esta función ahora solo pasa la prescripción al componente Prescription
    // El modal se manejará desde el componente Prescription individual
    console.log('Editando visibilidad de prescripción:', prescription)
  }

  if (!groups || !Array.isArray(groups.results) || !fuse.current) return null

  // Señalización beta (pedido de Nacho, 2026-08-10 — mismo módulo/criterio
  // que ARCA): la solapa fija de feedback es position:fixed — montarla una
  // vez por cada Consultation de la lista (ahí vive el badge, por
  // evolución) duplicaría la solapa en pantalla si hay 2+ videoconsultas
  // con resumen de IA visibles a la vez. Se monta UNA sola vez acá, gateada
  // por si esta lista tiene al menos una evolución generada por IA — cada
  // BetaBadge de Consultation.js recibe onOpenAiSummaryFeedback para abrir
  // ESTA misma instancia (vía ref) en vez de montar la suya propia (mismo
  // bug real encontrado y corregido en las 4 páginas de ARCA: 2+ instancias
  // de BetaFeedback para el mismo featureKey a la vez).
  const hasAiSummary = Array.isArray(props.consultations.groups) &&
    props.consultations.groups.some(g => (props.consultations[g]?.evolutions || []).some(e => e.aiGenerated))

  const hasFiles = props.filters.files && Array.isArray(props.files) && props.files.length > 0

  return (
    <>
      {hasAiSummary && <BetaFeedback ref={aiSummaryFeedbackRef} featureKey="resumen_ia" featureName={t('ehr:ai_summary_feature_name', 'Resumen de videoconsulta con IA')} />}

      {/* Wrapper flex-column: arriba van fijas (flex-shrink-0) las pills de
          features Trial (reubicación 2026-09-01) y el buscador; abajo, UN solo
          contenedor scrolleable (flex-grow-1 + minHeight:0) que se lleva el
          alto disponible. `d-print-block` (mismo patrón que EHR.js) es
          obligatorio: sin eso "Imprimir HCE" mete un contenedor flex en medio
          del page-breaking. */}
      <div className={`d-flex flex-column h-100 d-print-block ${styles.printAutoHW}`}>
        {/* `d-flex` propio (no hereda flex-column del padre): dentro de un
            flex-column, `align-items: stretch` estiraría la pill a los 400px
            de ancho de la columna en vez de dejarla con su ancho natural.
            Gateado por `props.trialTools` para no dejar un `mb-2` fantasma
            de 8px en teams sin la beta. */}
        {props.trialTools && <div className="d-flex mb-2 d-print-none flex-shrink-0">{props.trialTools}</div>}
        {props.filters.records && (
          <div className="bg-white d-flex align-items-center shadow-sm mb-2 rounded-md position-relative d-print-none flex-shrink-0">
            <div className="position-absolute start-0 ms-3 top-0 mt-1">
              <IconSearch className="text-secondary" />
            </div>
            <input
              type="search"
              className="form-control border-0"
              style={{ paddingLeft: '43px' }}
              placeholder={t('ehr:finder_records', 'Buscador de evoluciones...')}
              value={query}
              onChange={search}
            />
          </div>
        )}
        {/* Card #911: recetas y evoluciones comparten UN solo contenedor
            scrolleable — antes eran 2 áreas de scroll hermanas peleándose el
            alto con flex-grow (en una HCE típica, con muchas más evoluciones
            que recetas, recetas quedaba comprimida a una franja ilegible).
            Ahora cada sección va a su alto natural y se recorre todo de corrido
            con una sola barra; recetas siempre legible. */}
        <div className={`overflow-auto d-flex flex-column gap-3 flex-grow-1 ${styles.printAutoHW}`} style={{ minHeight: 0 }}>
        {props.prescriptions?._form && (
          <Prescriptions
            prescriptions={props.prescriptions.docs || []}
            user={props.user}
            onEditVisibility={handleEditPrescriptionVisibility}
            resourceEhrConfig={props.resourceEhrConfig}
            resource={props.resource}
            resources={resources}
          />
        )}
        {hasFiles && (
          <div className="py-2 px-3 bg-white shadow-sm d-print-none">
            <h6>{t('files')}</h6>
            {(showAllFiles ? props.files : props.files.slice(0, 3)).map((file, index) => (
              <Archive
                key={file.id}
                file={file}
                className={showAllFiles || index <= 2 ? '' : 'd-none'}
                user={props.user.state}
              />
            ))}
            {!showAllFiles && props.files.length > 3 && (
              <div className="border-top pt-2 mt-2">
                <button
                  className="btn btn-sm link-primary bg-light border"
                  onClick={() => setShowAllFiles(true)}
                >{t('ehr:show_all_files', 'Mostrar todos los archivos')} <sup className="text-secondary">{props.files.length}</sup></button>
              </div>
            )}
          </div>
        )}

        {props.filters.records && (
          <>
            {/* Feedback de reunión 2026-08-19: durante los minutos que tarda
                el pipeline de resumen de IA (meet -> api), el profesional no
                tenía ningún indicio de que algo se está procesando. Placeholder
                en el mismo lugar donde va a aparecer la evolución real, arriba
                del todo (orden más-reciente-primero) — se lo saca solo apenas
                aiSummaryPending pasa a false (ver el refetch en EHR.js), nunca
                a mano. */}
            {props.aiSummaryPending && (
              // Bug real reportado en prod (2026-09-08): sin `flex-shrink-0` en el
              // spinner y `minWidth:0` en el texto, un flex item de texto largo NO
              // se achica por debajo de su ancho intrínseco (default `min-width:auto`
              // de flexbox) — en un contenedor angosto (panel lateral de la HCE) el
              // texto se corta en vez de hacer wrap a la línea siguiente.
              <div className="bg-white shadow-sm p-3 d-flex align-items-center gap-2 text-secondary">
                <div className="spinner-border spinner-border-sm flex-shrink-0" role="status" aria-hidden="true"></div>
                <div className="text-sm" style={{ minWidth: 0 }}>
                  {t('ehr:ai_summary_processing', 'Procesando el resumen de la videoconsulta con IA — va a aparecer acá en unos minutos.')}
                </div>
              </div>
            )}
            {/* Card V3 (feedback Lucio, 2026-08-28): antes, terminar bien o mal
                se veía IGUAL (el cartel de arriba solo desaparecía) — este
                bloque es lo que faltaba para distinguirlos. Gris/tranquilizador
                (alert-secondary), no alert-danger — mismo criterio de tono que
                HceSummary.js para una feature beta. */}
            {!props.aiSummaryPending && props.aiSummaryOutcome && (
              <div className="alert alert-secondary py-2 px-3 small d-flex align-items-center gap-2 mb-0" data-testid="ai-summary-outcome">
                {t(
                  `ehr:ai_summary_outcome_${props.aiSummaryOutcome.status}`,
                  AI_SUMMARY_FAILURE_LABEL[props.aiSummaryOutcome.status] || 'No se pudo generar el resumen automático.'
                )}
              </div>
            )}
          <Suspense fallback={<div className="bg-white shadow-sm p-3 text-center"><div className="spinner-border text-primary"></div> <div className="mt-2">Cargando consultas...</div></div>}>
            {groups.results.slice(0, consultationsNumber).map(result => (
              <LazyConsultation
                key={result.group}
                user={props.user}
                resource={props.resource}
                consultation={props.consultations[result.group]}
                onRecordUpdate={props.onRecordUpdate}
                resourceEhrConfig={props.resourceEhrConfig}
                resources={resources}
                onOpenAiSummaryFeedback={() => aiSummaryFeedbackRef.current?.openManual()}
          />
            ))}
          </Suspense>
          </>
        )}
        {groups.results.length > consultationsNumber && (
          <div className="border-top pt-2 mt-2 d-flex gap-2 justify-content-center">
            <button
              className="btn btn-sm link-primary bg-light border"
              onClick={() => setConsultationsNumber(consultationsNumber + CONSULTATIONS_STEP)}
            >{t('ehr:show_more_records', 'Mostrar más evoluciones')}

            </button>
          </div>
        )}
        </div>
      </div>
    </>
  )
}

export default Consultations;
